// Global Instructions Rule Applied!
//
// Company short descriptions: a company's short description (read and sign
// off; the company form's edits go through the InputManager auto-patch), and
// the admins' status, Generate and bulk review (list, sign off, regenerate,
// edit). Every response is validated before it reaches a caller.
import { buildFullUrl, getBackendBaseUrl, API_ROUTES, type RouteDefinition } from '../../../core/utils/endpoints.ts'
import {
  validateAgentWrittenShortDescriptions,
  validateCompanyShortDescription,
  validateCompanyShortDescriptionsStatus,
  validateShortDescriptionsBatch,
  validateShortDescriptionsSignOff,
  type AgentWrittenShortDescription,
  type CompanyShortDescription,
  type CompanyShortDescriptionsStatus,
  type ShortDescriptionsBatch,
  type ShortDescriptionsSignOff
} from '../../../state_manager/data_models/company_short_description.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface ServiceResult<T> {
  data?: T
  error?: string
}

async function call<T>(
  route: RouteDefinition,
  accessToken: string | null,
  validate: (data: unknown) => T,
  pathParams: Record<string, string> = {},
  body?: unknown
): Promise<ServiceResult<T>> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, pathParams), {
      method: route.method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {})
    })
    if (!response.ok) {
      if (response.status === 401) throw new Error('Authentication failed - please log in again')
      const errData = (await response.json().catch(() => ({}))) as { message?: string; error?: string }
      throw new Error(errData.message || errData.error || response.statusText)
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validate(json.data) }
  } catch (e) {
    return { error: e instanceof Error ? e.message : 'Request failed' }
  }
}

export const fetchCompanyShortDescription = (accountId: string, accessToken: string | null) =>
  call<CompanyShortDescription>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTION,
    accessToken,
    validateCompanyShortDescription,
    { id: accountId }
  )

export const signOffCompanyShortDescription = (accountId: string, accessToken: string | null) =>
  call<CompanyShortDescription>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTION_SIGN_OFF,
    accessToken,
    validateCompanyShortDescription,
    { id: accountId },
    {}
  )

export const fetchShortDescriptionsStatus = (accessToken: string | null) =>
  call<CompanyShortDescriptionsStatus>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTIONS_STATUS,
    accessToken,
    validateCompanyShortDescriptionsStatus
  )

export const generateShortDescriptions = (includeDeclined: boolean, accessToken: string | null) =>
  call<ShortDescriptionsBatch>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTIONS_GENERATE,
    accessToken,
    validateShortDescriptionsBatch,
    {},
    { data: { include_declined: includeDeclined } }
  )

export const fetchAgentWrittenShortDescriptions = (accessToken: string | null) =>
  call<AgentWrittenShortDescription[]>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTIONS_AGENT_WRITTEN,
    accessToken,
    validateAgentWrittenShortDescriptions
  )

export const signOffShortDescriptions = (accountIds: string[], accessToken: string | null) =>
  call<ShortDescriptionsSignOff>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTIONS_SIGN_OFF,
    accessToken,
    validateShortDescriptionsSignOff,
    {},
    { data: { account_ids: accountIds } }
  )

export const regenerateShortDescriptions = (accountIds: string[], accessToken: string | null) =>
  call<ShortDescriptionsBatch>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTIONS_REGENERATE,
    accessToken,
    validateShortDescriptionsBatch,
    {},
    { data: { account_ids: accountIds } }
  )

/** A person's edit (or clear), as the company form's auto-patch sends it. */
export const setCompanyShortDescription = (accountId: string, text: string, accessToken: string | null) =>
  call<CompanyShortDescription>(
    API_ROUTES.COMPANIES.SHORT_DESCRIPTION_PATCH,
    accessToken,
    validateCompanyShortDescription,
    {},
    { data: { id: accountId, short_description: text } }
  )
